/**
 * Does this load of a bid's page count as the customer opening it?
 * (Owner, Oct 20 2026: "give me a notification if client opens the bid" —
 * server/services/bidOpened.js texts him on the first open.)
 *
 * Only the browser can tell the customer from the office: the server sees the
 * same request either way. So the page asks, once per load, after the bid has
 * been on screen about 3 seconds:
 *   - not inside a frame — the office's Preview shows this page in an iframe;
 *   - nobody signed in to the app on this browser — the owner tapping his own
 *     link, a bidder showing it on site;
 *   - not a link opened from the app — every office link to the page carries
 *     ?from=office (officeLink below), which the page reads and then takes off
 *     the address bar so a copied link never carries it;
 *   - the tab is on screen — a prerender or a hidden tab is nobody reading it;
 *   - not the bounce back from the card or deposit page — that is the same
 *     visit coming home, not another open.
 * Pure, so the rule reads in one place.
 */
export const OPENED_AFTER_MS = 3000;

export type OpenedContext = { inFrame: boolean; staffSignedIn: boolean; fromOffice: boolean; visible: boolean; backFromPayment: boolean };

export function shouldSendOpened(c: OpenedContext): boolean {
  return !c.inFrame && !c.staffSignedIn && !c.fromOffice && c.visible && !c.backFromPayment;
}

/** This browser's answers. Anything the browser refuses to say is read the safe way (a frame we cannot see out of is a frame). */
export function openedContext(backFromPayment: boolean, fromOffice = false): OpenedContext {
  let inFrame = true;
  try { inFrame = window.self !== window.top; } catch { inFrame = true; }
  let staffSignedIn = false;
  try { staffSignedIn = !!window.localStorage.getItem('token'); } catch { staffSignedIn = false; }
  return { inFrame, staffSignedIn, fromOffice, visible: document.visibilityState === 'visible', backFromPayment };
}

/** An office link to a bid's page: marked, so opening it never counts as the customer opening it. Never for a link sent to a customer. */
export function officeLink(url: string): string {
  if (!url) return url;
  try {
    const u = new URL(url, window.location.origin);
    if (!/^\/q\//.test(u.pathname)) return url;
    u.searchParams.set('from', 'office');
    return /^https?:/i.test(url) ? u.toString() : `${u.pathname}${u.search}${u.hash}`;
  } catch { return url; }
}

/** Opened from an office link? */
export function cameFromOffice(search: string): boolean {
  return new URLSearchParams(search).get('from') === 'office';
}

/** Take ?from=office off the address bar, so a link copied from this tab is the customer's plain link. */
export function stripOfficeMark(): void {
  try {
    const u = new URL(window.location.href);
    if (!u.searchParams.has('from')) return;
    u.searchParams.delete('from');
    window.history.replaceState(window.history.state, '', `${u.pathname}${u.search}${u.hash}`);
  } catch { /* the address keeps it; nothing else changes */ }
}

/** The card and deposit pages send them back with ?card= or ?deposit= (read before the page tidies the address). */
export function cameBackFromPayment(search: string): boolean {
  const q = new URLSearchParams(search);
  return q.has('card') || q.has('deposit');
}
